import {Injectable} from '@angular/core';
import {combineLatest, firstValueFrom, from, lastValueFrom, Observable} from 'rxjs';
import {TenantService} from './tenant.service';
import {CourseSection} from '../models/course-section.model';
import {createFirestoreId, findLastBySeqNo, findUniqueMatchWithId, readCollectionWithIds, readDocumentWithId} from '../common/firestore-utils';
import {concatMap, first, map} from 'rxjs/operators';
import {Lesson} from '../models/lesson.model';
import {Course} from '../models/course.model';
import {Update} from '@ngrx/entity';
import {UpdateStr} from '@ngrx/entity/src/models';
import {increment} from '@firebase/firestore';
import { collection, collectionData, deleteDoc, doc, docData, Firestore, limit, orderBy, query, setDoc, updateDoc, where, writeBatch } from '@angular/fire/firestore';
import {CustomPage} from "../models/page-builder/custom-page.model";

@Injectable()
export class LessonsDBService {

  constructor(private tenant: TenantService,
              private firestore: Firestore) {

  }

  findLessonById(courseId:string, lessonId:string): Observable<Lesson> {

    const lessonPath = this.lessonsPath(courseId) + `/${lessonId}`;

    return readDocumentWithId<Lesson>(docData(doc(this.firestore, lessonPath), {idField: "id"})).pipe(first());
  }


  loadCourseSection(courseId:string, sectionId:string) {
    return readDocumentWithId(docData(doc(this.firestore,this.sectionsPath(courseId) + '/' + sectionId ), {idField: "id"})).pipe(first());

  }

  loadCourse(courseId: string) {

    return readDocumentWithId(docData(doc(this.firestore, this.tenant.path(`courses/${courseId}`) ), {idField: "id"})).pipe(first());

  }


  loadCourseSections(courseId: string): Observable<CourseSection[]> {

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      orderBy('seqNo')
    ]

    const courseSectionsQuery = query(collection(this.firestore, coursePath + '/sections'), ...constraints)

    const courseSectionsData = collectionData(courseSectionsQuery, {idField: 'id'});


    return readCollectionWithIds<CourseSection[]>(
      courseSectionsData
      )
      .pipe(
      map(sections => sections.map(section => {return {...section, courseId}}))
      );

  }

  loadCourseLessons(courseId: string): Observable<Lesson[]> {

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      orderBy('seqNo')
    ]

    const courseLessonsData = collectionData(query(collection(this.firestore, coursePath + '/lessons'), ...constraints), {idField: "id"})

    return readCollectionWithIds<Lesson[]>(courseLessonsData);

    // return readCollectionWithIds<Lesson[]>(this.afs.collection(coursePath + '/lessons', ref => ref.orderBy('seqNo')));

  }

  loadCoursesLessonsCount(courseId: string){

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      where("status", "==", "published"),
    ]

    const courseLessonsData = collectionData(query(collection(this.firestore, coursePath + '/lessons'), ...constraints), {idField: "id"})
    return courseLessonsData
      .pipe(
        map(snaps => snaps.length?.toString()),
        first()
      )
  }

  async loadCoursesLessonsPublishedCount(coursesIds: string[]){

    let count = 0;

    for (const courseId of coursesIds) {

      const coursePath = this.tenant.path(`courses/${courseId}`);
      const constraints = [
        where("status", "==", "published"),
      ]

      const courseLessonsData = await lastValueFrom(
        collectionData(query(collection(this.firestore, coursePath + '/lessons'), ...constraints), {idField: "id"}).pipe(first())
    )

      count += courseLessonsData.length

    }

    return count

  }


  addNewSection(course: Course, title: string):Observable<CourseSection> {
    return findLastBySeqNo<CourseSection>(this.firestore, this.sectionsPath(course.id))
      .pipe(
        concatMap(last => {

          const newSection: any = {
            title,
            seqNo: last ? (last.seqNo + 1) : 1
          };

            const docRef = doc(this.firestore, this.sectionsPath(course.id) + '/' + createFirestoreId(this.firestore))
            const addSectionAsync = setDoc(
              docRef,
              newSection
            ).then(() => {
              return {
                id: docRef.id,
                courseId: course.id,
                ...newSection
              }
            })

          return from(addSectionAsync);
        })
      );
  }

  saveSection(courseId: string, sectionId:string, update: UpdateStr<CourseSection>): Observable<any> {

    const sectionDocRef = doc(this.firestore, this.sectionsPath(courseId), update.id)

    const updateRef = updateDoc(sectionDocRef, update.changes)

    return from(updateRef);

  }


  addNewLesson(courseId: string, sectionId: string, title: string, type: string, newLessonSeqNo:number, changes?: Partial<Lesson>, lessonId?: string): Observable<Lesson> {

    const calendarBookingDefaults = type === 'calendar-booking' ? {
      calendarBookingButtonText: "Book Now",
      calendarBookingButtonTextColor: "#ffffff",
      calendarBookingButtonColor: "#EB3B3B",
      calendarBookingButtonIconColor: "#ffffff"
    } : {};

    const newLesson: any = {
      title,
      type,
      seqNo: newLessonSeqNo,
      sectionId,
      status: 'draft',
      free:false,
      ...calendarBookingDefaults,
      ...changes

    };

    let lessonFirestoreId: string;

    if (lessonId) {
      lessonFirestoreId = lessonId;
    } else {
      lessonFirestoreId = createFirestoreId(this.firestore);
    }

    const docRef = doc(this.firestore, this.lessonsPath(courseId), lessonFirestoreId)
    const addLessonAsync = setDoc(docRef, newLesson).then(() => {
      return {
        id: docRef.id,
        ...newLesson
      }
    })

    return from(addLessonAsync);
  }

  saveLesson(courseId: string, update: UpdateStr<Lesson>): Observable<any> {

    const saveLessonAsync = updateDoc(doc(this.firestore, this.lessonsPath(courseId), update.id), update.changes)

    return from(saveLessonAsync);

  }

  deleteSection(courseId: string, sectionId: string): Observable<any> {

    return from(deleteDoc(doc(this.firestore, this.sectionsPath(courseId), sectionId)))

  }

  deleteLesson(courseId: string, lessonId:string, videoDuration:number): Observable<any> {

    const batch = writeBatch(this.firestore);

    const lessonRef = doc(this.firestore,this.lessonsPath(courseId), lessonId)

    batch.delete(lessonRef);

    if (videoDuration) {
      const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}`);

      batch.update(courseRef, {
        totalDuration: increment(-1 * videoDuration)
      });
    }

    return from(batch.commit());
  }

  private findLastLessonInSection(courseId:string, sectionId:string): Observable<Lesson> {

    const constraints = [
      where('sectionId', '==', sectionId),
      orderBy('seqNo', 'desc'),
      limit(1)
    ]

    const query$ = collectionData(
      query(
        collection(this.firestore, this.lessonsPath(courseId)), ...constraints),
      {idField: "id"}
      )

    // const query$ = this.afs.collection<Lesson>(this.lessonsPath(courseId),
    //     ref => ref.where('sectionId', '==', sectionId).orderBy('seqNo', 'desc').send-limit-email(1));

    return findUniqueMatchWithId(query$).pipe(first()) as Observable<Lesson>;
  }


  suscribeToLesson(courseId: string, lessonId:string): Observable<Lesson> {
    const lessonsData = docData(doc(this.firestore, this.lessonsPath(courseId) + '/' + lessonId), {idField: "id"})
    return readDocumentWithId(lessonsData);
  }


  private sectionsPath(courseId:string) {
    return this.tenant.path(`courses/${courseId}/sections`);
  }

  private lessonsPath(courseId:string) {
    return this.tenant.path(`courses/${courseId}/lessons`);
  }

  addLessonsSections(courseId:string, lessons: Lesson[], sections: CourseSection[]) {

    const batch = writeBatch(this.firestore);

    lessons.forEach(lesson => {
      const lessonRef = doc(this.firestore, this.lessonsPath(courseId), lesson.id);
      batch.set(lessonRef, lesson);
    });

    sections.forEach(section => {
      const sectionRef = doc(this.firestore, this.sectionsPath(courseId), section.id);
      batch.set(sectionRef, section);
    });

    return from(batch.commit());

  }

  updateLessons(courseId:string, changes: Update<Lesson>[]) {

    const batch = writeBatch(this.firestore);

    changes.forEach(change => {
      const lessonRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}/lessons/${change.id}`);
      batch.update(lessonRef, change.changes);
    });

    return from(batch.commit());

  }
}




